import { Controller } from "@hotwired/stimulus";
import { CircleAlert, CircleCheck, createElement, X } from "lucide";

// Lucide's createElement returns a bare <svg>; add the class that sizes it to
// the inherited font size, matching the server-rendered icons.
const icon = (node, ...classes) => {
  const svg = createElement(node);
  svg.classList.add("icon", ...classes);
  return svg;
};

/**
 * Generic model form controller that handles AJAX form submissions for create/update flows.
 * Supports automatic switching from create mode to edit mode after successful creation.
 *
 * Usage:
 *   <div data-controller="model-form"
 *        data-model-form-model-name-value="Note"
 *        data-model-form-show-url-template-value="/notes/:id">
 *     <div data-model-form-target="container">
 *       <form data-model-form-target="form" data-action="submit->model-form#handleFormSubmit">
 *         <!-- form fields -->
 *       </form>
 *     </div>
 *   </div>
 *
 * For creates: Expects JSON response with model_id or {model_name}_id field
 * For updates: Expects HTML response that replaces the container content
 */
export default class extends Controller {
  static targets = ["container", "form", "flashContainer"];
  static values = {
    modelName: String,
    createUrl: String,
    showUrlTemplate: String, // e.g., "/notes/:id" where :id will be replaced
  };

  isSubmitting = false;

  connect() {
    console.log(`${this.modelNameValue} form controller connected`);

    // Setup form handling if a form is available
    if (this.hasFormTarget) {
      console.log("Form target found, setting up form handling");
    }

    // Listen for audio capture completion if this is a Recording model
    if (this.modelNameValue === "Recording") {
      document.addEventListener(
        "audio-capture:completed",
        this.handleRecordingCompleted.bind(this),
      );
    }
  }

  disconnect() {
    console.log(`${this.modelNameValue} form controller disconnected`);

    // Clean up audio capture listener
    if (this.modelNameValue === "Recording") {
      document.removeEventListener(
        "audio-capture:completed",
        this.handleRecordingCompleted,
      );
    }
  }

  handleRecordingCompleted(event) {
    const recordingId = event.detail.recordingId;

    // Store the recording ID for later use
    this.element.setAttribute("data-model-id", recordingId);

    // Show success flash message for recording creation
    this.showFlashMessage("success", "Recording created successfully!");

    // Fetch and replace the interface with the form from the server
    this.fetchModelForm(recordingId);
  }

  handleFormSubmit(event) {
    console.log("Form submission intercepted", event);

    // Verify this is our form target
    if (!this.hasFormTarget) {
      console.error("No form target found, but form submission was triggered");
      return false;
    }

    // Check if we're in a modal
    const inModal = this.element.closest('[data-controller="modal"]');
    console.log("In modal context:", !!inModal);

    // If we're not in a modal, let the form submit normally
    if (!inModal) {
      console.log("Not in modal context, allowing normal form submission");
      return true; // Allow normal form submission
    }

    // In a modal, prevent default and handle asynchronously
    event.preventDefault();

    // Check if we're already submitting
    if (this.isSubmitting) {
      console.warn("Already submitting, ignoring duplicate submission");
      return false;
    }

    console.log(
      `Processing asynchronous ${this.modelNameValue} form submission in modal`,
    );
    this.isSubmitting = true;

    // Get form data from our form target
    const form = this.formTarget;
    const formData = new FormData(form);
    let url = form.getAttribute("action");
    let method = form.getAttribute("method") || "post";
    const modelId = this.element.getAttribute("data-model-id");

    // Determine if this is a create or update
    // If we have a model ID stored on the element, it's always an update
    const hasMethodPatch = formData.get("_method") === "patch";
    let isCreate =
      !modelId && !hasMethodPatch && method.toLowerCase() !== "patch";

    // If we have a model ID but the form isn't configured for update, fix it
    if (modelId && isCreate) {
      // Update the URL to point to the specific resource
      url = url.replace(/\/new$/, `/${modelId}`);
      method = "patch";
      formData.append("_method", "patch");
      isCreate = false; // Now it's definitely an update
    }

    // Add method override for Rails if needed
    if (method.toLowerCase() === "put" || method.toLowerCase() === "patch") {
      if (!formData.get("_method")) {
        formData.append("_method", method);
      }
    }

    // Get the CSRF token (if available)
    const csrfTokenElement = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = csrfTokenElement ? csrfTokenElement.content : null;

    // Show loading state
    this.showLoading(form);

    // Prepare headers
    const headers = {
      "X-Requested-With": "XMLHttpRequest",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    if (isCreate) {
      headers["Accept"] = "application/json";
    } else {
      headers["Accept"] = "text/html";
    }

    // Send AJAX request
    fetch(url, {
      method: method.toLowerCase() === "get" ? "get" : "post",
      body: method.toLowerCase() === "get" ? null : formData,
      headers: headers,
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }

        if (isCreate) {
          return response.json();
        } else {
          return response.text();
        }
      })
      .then((data) => {
        // Reset submitting flag
        this.isSubmitting = false;

        // Hide loading state
        this.hideLoading(form);

        if (isCreate) {
          // For creates, use the model ID from JSON response and fetch the edit form
          const modelId = data.note_id || data.recording_id || data.model_id;
          if (modelId) {
            this.element.setAttribute("data-model-id", modelId);
            this.showFlashMessage(
              "success",
              `${this.modelNameValue} created successfully!`,
            );
            this.fetchModelForm(modelId);
          } else {
            console.error("Could not extract model ID from create response");
            this.showFlashMessage(
              "error",
              `Error creating ${this.modelNameValue.toLowerCase()}`,
            );
          }
        } else {
          // For updates, replace content and show success message
          this.replaceContainerContent(data);
          this.extractAndShowFlashMessages(data);
        }
      })
      .catch((error) => {
        console.error(
          `Error submitting ${this.modelNameValue.toLowerCase()} form:`,
          error,
        );
        this.isSubmitting = false;
        this.hideLoading(form);

        // Show specific error message based on error type
        let errorMessage = `An error occurred while saving the ${this.modelNameValue.toLowerCase()}. Please try again.`;
        if (error.message.includes("400")) {
          errorMessage =
            "Invalid data provided. Please check your input and try again.";
        } else if (error.message.includes("500")) {
          errorMessage = "Server error occurred. Please try again later.";
        } else if (
          error.message.includes("NetworkError") ||
          error.name === "TypeError"
        ) {
          errorMessage =
            "Network connection error. Please check your connection and try again.";
        }

        this.showFlashMessage("error", errorMessage);

        // Try to refresh the form if there was an error and we have a model ID
        const modelId = this.element.getAttribute("data-model-id");
        if (modelId) {
          this.fetchModelForm(modelId);
        }
      });
  }

  showLoading(form) {
    // Disable submit buttons
    form
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((button) => {
        button.disabled = true;
        button.classList.add("opacity-50", "cursor-not-allowed");

        // Store original text and add loading spinner
        if (!button.hasAttribute("data-original-text")) {
          button.setAttribute("data-original-text", button.innerHTML);
          button.innerHTML = `<span class="inline-block animate-spin motion-reduce:animate-none mr-2">↻</span> Saving...`;
        }
      });
  }

  hideLoading(form) {
    // Re-enable submit buttons
    form
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((button) => {
        button.disabled = false;
        button.classList.remove("opacity-50", "cursor-not-allowed");

        // Restore original text
        if (button.hasAttribute("data-original-text")) {
          button.innerHTML = button.getAttribute("data-original-text");
          button.removeAttribute("data-original-text");
        }
      });
  }

  fetchModelForm(modelId) {
    // Get CSRF token (if available)
    const csrfTokenElement = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = csrfTokenElement ? csrfTokenElement.content : null;

    // Construct the show URL from the template
    const showUrl = this.showUrlTemplateValue.replace(":id", modelId);

    // Prepare headers
    const headers = {
      Accept: "text/html",
      "X-Requested-With": "XMLHttpRequest",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    // Fetch the model component with the persisted model
    fetch(showUrl, {
      headers: headers,
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        // Replace the container content with the model interface
        this.replaceContainerContent(html);
      })
      .catch((error) => {
        console.error(
          `Error fetching ${this.modelNameValue.toLowerCase()}:`,
          error,
        );
        this.showFlashMessage(
          "error",
          `Error loading ${this.modelNameValue.toLowerCase()} form`,
        );
      });
  }

  replaceContainerContent(html) {
    console.log(
      `Replacing ${this.modelNameValue.toLowerCase()} container content`,
    );

    // Parse the HTML response
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Look for the model form component in the response
    const modelFormComponent = doc.querySelector(
      `[data-controller*="model-form"][data-model-form-model-name-value="${this.modelNameValue}"]`,
    );

    if (modelFormComponent) {
      // Get the container content (everything inside the model-form container)
      const containerContent = modelFormComponent.querySelector(
        '[data-model-form-target="container"]',
      );

      if (containerContent && this.hasContainerTarget) {
        // Replace our container content with the new content
        this.containerTarget.innerHTML = containerContent.innerHTML;
        console.log(
          `Successfully replaced container content with ${this.modelNameValue.toLowerCase()} form`,
        );

        // Re-initialize any Stimulus controllers
        const event = new CustomEvent("turbo:load", {
          bubbles: true,
        });
        document.dispatchEvent(event);
      } else {
        console.error(
          `Could not find container content in ${this.modelNameValue.toLowerCase()} response`,
        );
      }
    } else {
      console.error(
        `Could not find model-form component for ${this.modelNameValue} in response`,
      );
      console.log("Response HTML:", html.substring(0, 500) + "...");
    }
  }

  extractAndShowFlashMessages(html) {
    // Parse the HTML response to look for flash messages
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Look for flash messages in the response
    const flashMessages = doc.querySelectorAll(
      ".flash-message, .alert, .notice",
    );

    // Clear any existing flash messages
    if (this.hasFlashContainerTarget) {
      this.flashContainerTarget.innerHTML = "";
    }

    // If we find flash messages in the response, display them
    if (flashMessages.length > 0) {
      flashMessages.forEach((message) => {
        if (this.hasFlashContainerTarget) {
          this.flashContainerTarget.appendChild(message.cloneNode(true));
        }
      });
    }
  }

  showFlashMessage(type, message) {
    if (!this.hasFlashContainerTarget) return;

    // Clear any existing flash messages
    this.flashContainerTarget.innerHTML = "";

    // Create flash message element
    const flashElement = document.createElement("div");
    flashElement.className = this.getFlashMessageClasses(type);
    flashElement.setAttribute("role", type === "error" ? "alert" : "status");

    const row = document.createElement("div");
    row.className = "flex items-center space-x-3";
    row.appendChild(icon(type === "error" ? CircleAlert : CircleCheck));

    const text = document.createElement("span");
    text.textContent = message;
    row.appendChild(text);

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "flash-close-button ml-auto";
    closeButton.setAttribute("aria-label", "Dismiss message");
    closeButton.appendChild(icon(X, "text-sm"));
    closeButton.addEventListener("click", () => flashElement.remove());
    row.appendChild(closeButton);

    flashElement.appendChild(row);
    this.flashContainerTarget.appendChild(flashElement);

    // Flash messages now persist until manually closed - no auto-hide
  }

  getFlashMessageClasses(type) {
    const baseClasses =
      "flash-message rounded-lg p-4 mb-3 transition-all duration-300";

    switch (type) {
      case "success":
        return `${baseClasses} bg-green-50 dark:bg-green-950/40 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-900`;
      case "error":
        return `${baseClasses} bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-900`;
      case "notice":
        return `${baseClasses} bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-900`;
      case "warning":
        return `${baseClasses} bg-yellow-50 dark:bg-yellow-950/40 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-900`;
      default:
        return `${baseClasses} bg-surface-muted text-fg border border-line`;
    }
  }

  // Reset the interface to initial state
  reset() {
    console.log(`Resetting ${this.modelNameValue.toLowerCase()} interface`);

    // Reset submitting flag
    this.isSubmitting = false;

    // Remove any stored model ID
    this.element.removeAttribute("data-model-id");

    // Clear any flash messages when resetting
    if (this.hasFlashContainerTarget) {
      this.flashContainerTarget.innerHTML = "";
    }
  }
}
